import React, { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { inr } from "./_shared";

/** Read-only Purchase Orders panel on Project Detail.
 * Iter77 — creation happens on the Purchase Board now. This card just
 * shows what's already been raised against this project so Accounts and
 * Prod Coord have context without leaving the project page.
 */
export default function PurchaseOrdersCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts", "admin", "production_coordination"].includes(role);
  const canDelete = role === "super_admin";
  const [pos, setPos] = useState([]);

  const load = async () => {
    try { const { data } = await api.get(`/costing/project/${p.id}/purchase-orders`); setPos(data); }
    catch { /* noop */ }
  };
  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, []);
  if (!canView) return null;

  const del = async (id) => {
    if (!window.confirm("Delete this Purchase Order?")) return;
    try { await api.delete(`/costing/project/${p.id}/purchase-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const pdfUrl = (id) => `${api.defaults.baseURL}/costing/project/${p.id}/purchase-orders/${id}.pdf`;

  return (
    <div className="hk-card p-4" data-testid="purchase-orders-card">
      <div className="flex items-center justify-between mb-2">
        <div className="hk-tiny-label">Purchase orders</div>
        <a href="/costing/purchase-board"
          className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900"
          data-testid="po-go-to-board">
          Purchase Board →
        </a>
      </div>
      {pos.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="po-empty">
          No purchase orders yet. Raise one from the Purchase Board.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="po-list">
          {pos.map((po) => (
            <li key={po.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 font-mono">{po.po_number}</div>
                  <div className="text-slate-600">{po.vendor_name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {(po.lines || []).length} line{(po.lines || []).length === 1 ? "" : "s"} · <b>{inr(po.total)}</b>
                    {po.delivery_date && <> · Deliver {po.delivery_date}</>}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    By {po.created_by_name} · {new Date(po.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={pdfUrl(po.id)} target="_blank" rel="noopener noreferrer"
                    className="text-orange-700 hover:text-orange-900" title="Download PDF"
                    data-testid={`po-pdf-${po.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  {canDelete && (
                    <button type="button" onClick={() => del(po.id)}
                      data-testid={`po-del-${po.id}`}
                      className="text-red-600 hover:text-red-800" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
